import PropTypes from 'prop-types'
import { useMemo } from 'react'

import { Button } from '@/components/elements/button'
import { useInstructorView } from '@/pages/Accounts/Instructors/Students/InstructorViewContext'

/**
 * @param {Object} props
 * @param {boolean} [props.canProceed=false] - Whether the user can proceed to the next block
 * @param {Function} props.nextBlock - Function to call to proceed to the next block
 * @param {React.ReactNode} props.children - The content of the block
 * @param {string} props.title - The title of the block
 * @param {Object} props.block - The block data for the active block
 * @param {string} [props.buttonText="Next"] - The text to display on the proceed button
 * @param {boolean} [props.isLastBlock=false] - Whether this is the last block in the course
 * @param {React.ReactNode} [props.customButton] - Override the default proceed button
 * @param {string} [props.upNextTitle] - Hint shown beneath the Next button about the next block
 */
function Block({
  canProceed = false,
  nextBlock,
  children,
  title,
  block,
  isLastBlock,
  customButton,
  upNextTitle,
}) {
  const instructorView = useInstructorView()
  const buttonText = useMemo(() => {
    if (isLastBlock) return 'Finish module'
    if (block.type === 'quiz') return 'Next'
    return block.status !== 'Completed' && !!block.content.quiz ? 'Take Quiz' : 'Next'
  }, [isLastBlock, block])
  return (
    <div className="grid h-full max-h-full grid-rows-[min-content_1fr_min-content]">
      <h1 className="text-lg font-semibold">{title}</h1>
      <div className="mx-auto w-full max-w-5xl overflow-y-auto">{children}</div>
      <div className="flex flex-col items-center gap-1 p-1 lg:p-4">
        {customButton || (
          <Button
            data-testid="block-next-button"
            onClick={() => nextBlock()}
            disabled={!canProceed && !instructorView}
          >
            {buttonText}
          </Button>
        )}
        {!isLastBlock && upNextTitle && (
          <p className="text-muted-foreground text-xs">
            Up next: <span className="font-medium">{upNextTitle}</span>
          </p>
        )}
      </div>
    </div>
  )
}

export default Block

Block.propTypes = {
  canProceed: PropTypes.bool,
  nextBlock: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  block: PropTypes.object,
  isLastBlock: PropTypes.bool,
  customButton: PropTypes.node,
  upNextTitle: PropTypes.string,
}
